import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, CheckCircle, AlertCircle, Loader2, RefreshCw, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import useEmailConnection from '../../hooks/email/useEmailConnection';
import GmailOAuthSimple from '../auth/GmailOAuthSimple';

/**
 * Email Connection Test Component
 * Demonstrates the email service integration for Step 3
 */
const EmailConnectionTest = () => {
  const {
    isLoading,
    isConnected,
    connection,
    syncStatus,
    error,
    saveConnection,
    toggleSync,
    deleteConnection,
    refreshTokens,
    getDecryptedConnection,
    validateConnection,
    clearError,
    refresh
  } = useEmailConnection();

  const [testResults, setTestResults] = useState(null);

  const handleOAuthSuccess = async (oauthData) => {
    try {
      console.log('OAuth Success - Saving to database:', oauthData);
      await saveConnection(oauthData);
      setTestResults({
        type: 'success',
        message: 'Email connection saved successfully!',
        data: oauthData
      });
    } catch (error) {
      console.error('Error saving connection:', error);
      setTestResults({
        type: 'error',
        message: `Failed to save connection: ${error.message}`,
        data: error
      });
    }
  };

  const handleOAuthError = (error) => {
    console.error('OAuth Error:', error);
    setTestResults({
      type: 'error',
      message: `OAuth failed: ${error}`,
      data: error
    });
  };

  const handleToggleSync = async () => {
    try {
      const newSyncState = !syncStatus?.is_sync_enabled;
      await toggleSync(newSyncState);
      setTestResults({
        type: 'success',
        message: `Email sync ${newSyncState ? 'enabled' : 'disabled'} successfully!`,
        data: { sync_enabled: newSyncState }
      });
    } catch (error) {
      console.error('Error toggling sync:', error);
      setTestResults({
        type: 'error',
        message: `Failed to toggle sync: ${error.message}`,
        data: error
      });
    }
  };

  const handleDeleteConnection = async () => {
    if (!window.confirm('Are you sure you want to delete the email connection?')) {
      return;
    }

    try {
      await deleteConnection();
      setTestResults({
        type: 'success',
        message: 'Email connection deleted successfully!',
        data: null
      });
    } catch (error) {
      console.error('Error deleting connection:', error);
      setTestResults({
        type: 'error',
        message: `Failed to delete connection: ${error.message}`,
        data: error
      });
    }
  };

  const handleRefreshTokens = async () => {
    try {
      await refreshTokens();
      setTestResults({
        type: 'success',
        message: 'Tokens refreshed successfully!',
        data: null
      });
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      setTestResults({
        type: 'error',
        message: `Failed to refresh tokens: ${error.message}`,
        data: error
      });
    }
  };

  const handleGetDecryptedConnection = async () => {
    try {
      const decryptedConnection = await getDecryptedConnection();
      setTestResults({
        type: 'success',
        message: 'Decrypted connection retrieved successfully!',
        data: decryptedConnection
      });
    } catch (error) {
      console.error('Error getting decrypted connection:', error);
      setTestResults({
        type: 'error',
        message: `Failed to get decrypted connection: ${error.message}`,
        data: error
      });
    }
  };

  const handleValidateConnection = async () => {
    try {
      const isValid = await validateConnection();
      setTestResults({
        type: isValid ? 'success' : 'warning',
        message: `Connection validation: ${isValid ? 'Valid' : 'Invalid or expired'}`,
        data: { is_valid: isValid }
      });
    } catch (error) {
      console.error('Error validating connection:', error);
      setTestResults({
        type: 'error',
        message: `Failed to validate connection: ${error.message}`,
        data: error
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Email Service Test - Step 3
        </h1>
        <p className="text-gray-600">
          Test the database service layer for Gmail OAuth token management
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Connection Status
            </CardTitle>
            <CardDescription>
              Current email connection and sync status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2">Loading...</span>
              </div>
            ) : isConnected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm font-medium text-green-800">
                      Gmail Connected
                    </span>
                  </div>
                  <Badge variant="default">Active</Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{connection?.email_address}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Provider:</span>
                    <span className="font-medium capitalize">{connection?.provider}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sync Enabled:</span>
                    <Badge variant={syncStatus?.is_sync_enabled ? "default" : "secondary"}>
                      {syncStatus?.is_sync_enabled ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="font-medium">
                      {connection?.updated_at ? 
                        new Date(connection.updated_at).toLocaleString() : 
                        'Unknown'
                      }
                    </span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={handleToggleSync}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    {syncStatus?.is_sync_enabled ? (
                      <>
                        <ToggleRight className="h-4 w-4 mr-1" />
                        Disable Sync
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-4 w-4 mr-1" />
                        Enable Sync
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleDeleteConnection}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No Gmail connection</p>
                <GmailOAuthSimple
                  onSuccess={handleOAuthSuccess}
                  onError={handleOAuthError}
                  isLoading={isLoading}
                  buttonText="Connect Gmail Account"
                />
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
                <Button
                  onClick={clearError}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Operations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <RefreshCw className="h-5 w-5 mr-2" />
              Service Operations
            </CardTitle>
            <CardDescription>
              Test email service functions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-2">
              <Button
                onClick={handleRefreshTokens}
                disabled={!isConnected || isLoading}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Tokens
              </Button>
              
              <Button
                onClick={handleGetDecryptedConnection}
                disabled={!isConnected || isLoading}
                variant="outline"
                size="sm"
              >
                <Mail className="h-4 w-4 mr-2" />
                Get Decrypted Connection
              </Button>
              
              <Button
                onClick={handleValidateConnection}
                disabled={!isConnected || isLoading}
                variant="outline"
                size="sm"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Validate Connection
              </Button>
              
              <Button
                onClick={refresh}
                disabled={isLoading}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Data
              </Button>
            </div>

            {/* Test Results */}
            {testResults && (
              <div className={`p-3 rounded-lg border ${
                testResults.type === 'success' ? 'bg-green-50 border-green-200' :
                testResults.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center mb-2">
                  {testResults.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  ) : testResults.type === 'warning' ? (
                    <AlertCircle className="h-4 w-4 text-yellow-500 mr-2" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  )}
                  <span className={`text-sm font-medium ${
                    testResults.type === 'success' ? 'text-green-800' :
                    testResults.type === 'warning' ? 'text-yellow-800' :
                    'text-red-800'
                  }`}>
                    {testResults.message}
                  </span>
                </div>
                {testResults.data && (
                  <details className="text-xs">
                    <summary className="cursor-pointer font-medium">View Data</summary>
                    <pre className="mt-2 whitespace-pre-wrap text-gray-600">
                      {JSON.stringify(testResults.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Integration Status */}
      <Card>
        <CardHeader>
          <CardTitle>Step 3 Integration Status</CardTitle>
          <CardDescription>
            Database service layer implementation progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">✅</div>
              <div className="text-sm font-medium text-green-800">Token Encryption</div>
              <div className="text-xs text-green-600">Secure storage</div>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">✅</div>
              <div className="text-sm font-medium text-green-800">Database Operations</div>
              <div className="text-xs text-green-600">CRUD functions</div>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">✅</div>
              <div className="text-sm font-medium text-green-800">Token Management</div>
              <div className="text-xs text-green-600">Refresh & validation</div>
            </div>
            
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">⏳</div>
              <div className="text-sm font-medium text-yellow-800">N8N Integration</div>
              <div className="text-xs text-yellow-600">Step 4</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailConnectionTest;
